import { type GetBackNotificheParams } from '@framework/api-client/api/backoffice/schemas/getBackNotificheParams.ts';
import { type GetFrontNotificheParams } from '@framework/api-client/api/inquiries-compile/schemas/getFrontNotificheParams.ts';
import { SortByNotifica } from '@framework/api-client/api/inquiries-compile/schemas/sortByNotifica.ts';
import { SortDirection } from '@framework/api-client/api/inquiries-compile/schemas/sortDirection.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NotificationsLogic } from '../../../schema/notifications/NotificationLogic.ts';
import { useNotification } from './useNotification.ts';

const PAGE_SIZE = 10;

type GetNotificationParams = GetFrontNotificheParams | GetBackNotificheParams;

/**
 * Hook che centralizza la logica di stato e query per il pulsante notifiche
 * con drawer. Usato sia in frontoffice che in backoffice.
 */
export function useNotificationsButton() {
  const [visible, setVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // true: lette; false: da leggere; undefined: tutte
  const [filterToRead, setFilterToRead] = useState<boolean | undefined>(
    undefined,
  );

  const showNotificationsPage = NotificationsLogic.showListPage();

  const query = useMemo(() => {
    return {
      firstResult: (page - 1) * PAGE_SIZE,
      maxResults: PAGE_SIZE,
      sortField: SortByNotifica.DATA_NOTIFICA,
      sortDirection: SortDirection.DESC,
      flVista: showNotificationsPage ? false : filterToRead,
    } satisfies GetNotificationParams;
  }, [page, filterToRead, showNotificationsPage]);

  const { apiResult, notifications, notificationUpdate, meta, refetch } =
    useNotification(query);

  // Evito di azzerarlo quando cambio pagina
  useEffect(() => {
    if (meta.total == null) return;
    setTotal(meta.total);
  }, [meta.total]);

  const handleOpen = useCallback(() => {
    refetch();
    setVisible(true);
  }, [refetch]);

  const handleClose = useCallback(() => {
    notificationUpdate.resetUnreadFlag();
    setVisible(false);
    setPage(1);
  }, [notificationUpdate]);

  const drawerProps = {
    id: 'notifications-drawer' as const,
    handleClose,
    visible,
    filterToRead,
    setFilterToRead,
    setPage,
    total,
    page,
    pageSize: PAGE_SIZE,
    notifications,
    apiResult,
    showNotificationsPage,
  };

  return {
    unreadTotal: notificationUpdate.unreadTotal,
    handleOpen,
    drawerProps,
  };
}
